<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { getSubBus } from '@/platform/host-sdk/host-bridge';
import {
  replayAll,
  shellActive,
  subBundleMounted,
  viewportSize,
} from '@/platform/host-sdk/sub-broadcaster';
import { ACCOUNT_INFO_KEY } from '@/platform/identity/queries';
import { useUserStore } from '@/platform/identity/store';
import { queryClient } from '@/shared/lib/query-client';
import { SUB_EVENTS, onMessage, type SubProps } from '@celerdata/host-bridge-contract';
import WujieVue from 'wujie-vue3';

import type { AccountInfoResponse } from '@/platform/identity/api';

const route = useRoute();
const router = useRouter();
const user = useUserStore();

// Sub URL is same-origin (sub-path of v3 host).  Sharing the origin lets
// localStorage carry the auth token across v3 ↔ legacy, so legacy's hard
// redirect guard (`window.location.href = '/login'`) doesn't fire and wujie
// stays in sandbox mode.  Cross-origin deploy would isolate the storage and
// force degrade.
//
// `sync` is intentionally off — wujie's built-in sync writes `?legacy=…` to
// the host URL, breaking external bookmarks.  Path-level sync runs over the
// host-bridge bus instead (see below).
const subUrl = import.meta.env.VITE_LEGACY_SUB_URL;

// Element UI's vendored Popper miscalculates dropdown positions inside the
// wujie sandbox iframe.  Root cause is the popper.js v1.11-era source baked
// into element-ui 2.x; popper.js v1.16.1 has the iframe-positioning math
// right.  See patches/element-ui@2.15.9.patch: lib/utils/popper.js
// re-exports the npm `popper.js` package and lib/utils/vue-popper.js +
// lib/table.js are adapted to the slightly-changed instance API
// (`onCreate` moved to constructor option, `._popper` → `.popper`).
const wujiePlugins: never[] = [];

// Sub props (spec §6 + §4.0). Two flavors share the wire:
//   - `assign` / `replace`: same-tab hard navigation. wujie proxies
//     `location.*` inside the sub iframe, so a raw `location.assign`
//     in sub only navs the iframe. These bounce the call to the host's
//     real window instead.
//   - `getInitialState`: a getter sub invokes ONCE at module-eval to
//     seed its mirror synchronously, eliminating the cold-start flash
//     window bus-only replay couldn't cover.
const subProps: SubProps = {
  assign: (url) => {
    window.location.assign(url);
  },
  replace: (url) => {
    window.location.replace(url);
  },
  getInitialState: () => {
    const accountID = user.info?.accountID;
    const accountInfo = accountID
      ? (queryClient.getQueryData<AccountInfoResponse>([...ACCOUNT_INFO_KEY, accountID]) ?? null)
      : null;
    return {
      auth: user.info
        ? {
            user: user.info,
            perms: user.perms,
            dataPerms: user.dataPerms,
            accountInfo,
          }
        : null,
      viewport: viewportSize.value,
      path: { path: router.currentRoute.value.fullPath },
    };
  },
};

// Three-layer mask gate (spec §4.0.2). Order matters:
//   1. Blank protection — sub bundle hasn't rendered yet. Mask MUST stay,
//      regardless of any other state, otherwise host shows an empty iframe.
//      Hard fallback does NOT override this.
//   2. Hard fallback — sub has rendered but `user.info` hasn't arrived in
//      MASK_HARD_TIMEOUT_MS. Bounded escape for genuinely stuck hosts
//      (silent network failure, no 401 / 50001 redirect). Mask lifts in
//      degraded state.
//   3. Normal auth wait — sub rendered, no hard timeout fired, but
//      `user.info` still null (legacy layout depends on it). Stays until
//      bus `AUTH_CHANGED` fills it.
const MASK_HARD_TIMEOUT_MS = 5000;
const hardFallback = ref(false);
const alive = ref(true);
let hardFallbackTimer: ReturnType<typeof setTimeout> | null = null;

const showMask = computed(() => {
  if (!subBundleMounted.value) return true;
  if (hardFallback.value) return false;
  // authFree legacy routes (/terms-of-use, /status) are reached
  // explicitly without a session — waiting for user.info would peg the
  // mask up until the 5s hard fallback fires on every public visit. Once
  // the sub bundle has rendered, lift the mask immediately.
  if (route.meta.authFree) return false;
  return !user.info;
});

// Sub → host: legacy emits sub:path-changed after every router.afterEach
// AND from the sub's catch-all forwarder for host-owned paths. Mirror the
// sub's nav type: a push walks browser history; a replace (legacy's
// /redirect bounce) must replace here too, or the transient /redirect entry
// lingers and Back breaks. Safe against echo loops: both sides equality-guard
// on fullPath, so a reverse-sync from `subPath` no-ops instead of firing a
// redundant nav.
// Host → sub direction is handled by sub-broadcaster's `subPath` source
// emitting host:path-changed — no extra wiring needed here.
const offSubPath = onMessage(getSubBus(), SUB_EVENTS.PATH_CHANGED, (payload) => {
  if (route.fullPath === payload.path) return;
  if (payload.replace) router.replace(payload.path);
  else router.push(payload.path);
});

// shellActive gates host:* delta emissions in sub-broadcaster — on while this
// host component is mounted, off when the user enters a v3-native route.
// Backgrounded sub catches up via replayAll on the next wujie lifecycle event.
onMounted(() => {
  shellActive.value = true;
  hardFallbackTimer = setTimeout(() => {
    if (!alive.value) return; // double-check after unmount race
    hardFallback.value = true;
  }, MASK_HARD_TIMEOUT_MS);
});

// wujie activated fires on cold-start tail AND on every :alive remount.
// Replay duty is for `:alive` remount catch-up only — cold-start initial
// state is seeded by `getInitialState()` at sub module-eval (§4.0), not
// bus replay. The `shellActive = true` line is defensive: onBeforeUnmount
// may have flipped it false during a transition, and activated fires
// before that handler's effect settles on remount.
function handleActivated(): void {
  shellActive.value = true;
  replayAll();
}

onBeforeUnmount(() => {
  alive.value = false;
  if (hardFallbackTimer) {
    clearTimeout(hardFallbackTimer);
    hardFallbackTimer = null;
  }
  shellActive.value = false;
  offSubPath();
});
</script>

<template>
  <div class="bg-surface relative h-full w-full overflow-hidden">
    <div v-if="showMask" class="bg-surface absolute inset-0 z-10 flex items-center justify-center">
      <div class="grid-loader">
        <i></i><i></i><i></i> <i></i><i></i><i></i> <i></i><i></i><i></i>
      </div>
    </div>
    <WujieVue
      name="legacy"
      :url="subUrl"
      :sync="false"
      :plugins="wujiePlugins"
      :alive="true"
      :props="subProps"
      :activated="handleActivated"
      width="100%"
      height="100%"
      data-testid="legacy-shell-host"
    />
  </div>
</template>

<style scoped>
/* DOM-based loader ported from web_frontend's PR #1197 — transform/opacity
   animations run on the compositor thread, so the spinner keeps moving while
   the main thread is parsing the sub bundle. Geometry is the 56px-scaled
   copy of the legacy gif (cell 35, gap 7, radius 3). */
.grid-loader {
  position: relative;
  width: 56px;
  height: 56px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 3.27px;
}

.grid-loader i {
  background: #008ff4;
  border-radius: 1.4px;
  /* negative delay avoids a static first frame */
  animation:
    gridPulseScale 1.2s ease-in-out infinite,
    gridPulseFade 1.2s ease-in-out infinite;
  animation-delay: calc(var(--d) * 0.1s - 1.2s);
  will-change: transform, opacity;
}

/* Anti-diagonal wave: delay factor = row + column */
.grid-loader i:nth-child(1) {
  --d: 0;
}
.grid-loader i:nth-child(2),
.grid-loader i:nth-child(4) {
  --d: 1;
}
.grid-loader i:nth-child(3),
.grid-loader i:nth-child(5),
.grid-loader i:nth-child(7) {
  --d: 2;
}
.grid-loader i:nth-child(6),
.grid-loader i:nth-child(8) {
  --d: 3;
}
.grid-loader i:nth-child(9) {
  --d: 4;
}

/* Matches the original gif: scale holds at its minimum for ~20% of the
   cycle, opacity eases smoothly. */
@keyframes gridPulseScale {
  0%,
  100% {
    transform: scale(1);
  }
  40%,
  60% {
    transform: scale(0.63);
  }
}

@keyframes gridPulseFade {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}
</style>
